import { useMemo } from 'react';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, CalendarX } from 'lucide-react';
import type {
  FacilityFlowCoreModulesPlanningDtosPlanningEntriesPlanningEntryListDto,
  FacilityFlowCoreModulesPlanningDtosPlanningEntriesPlanningEntryTaskDto,
} from '../../../api/generated/models';
import { TimeRuler } from './TimeRuler';
import { TimelineRow } from './TimelineRow';
import { isBackOfficeRole } from '../../../utils/roles';
import { buildAxis, DEFAULT_RANGE, timeToMin } from '../../../utils/timeAxis';

type Entry = FacilityFlowCoreModulesPlanningDtosPlanningEntriesPlanningEntryListDto;
type EntryTask = FacilityFlowCoreModulesPlanningDtosPlanningEntriesPlanningEntryTaskDto;

interface EmployeeOption {
  id: string;
  name: string;
  avatarColor?: string | null;
  role?: string;
}

interface DayViewProps {
  date: Date;
  entries: Entry[];
  selectedEmployeeIds: string[];
  /** Optional roster used to render rows for selected employees that have no entry yet. */
  selectableEmployees?: EmployeeOption[];
  rangeStartHour?: number;
  rangeEndHour?: number;
  onAddTask?: (entry: Entry, startTime?: string) => void;
  onTaskClick?: (task: EntryTask, entry: Entry) => void;
  onAddWorkDay?: (employeeId: string, date: string) => void;
  onDueTaskDrop?: (
    taskId: string,
    employeeId: string,
    date: string,
    durationMinutes: number,
    startTime?: string,
  ) => void;
}

function entryIsAbsence(entry: Entry): boolean {
  return (entry.entryType ?? '').toLowerCase() === 'absence';
}

function countOverlaps(tasks: EntryTask[]): number {
  let conflicts = 0;
  for (let i = 0; i < tasks.length; i++) {
    const a = tasks[i];
    if (!a.startTime || !a.endTime) continue;
    const aStart = timeToMin(a.startTime);
    const aEnd = timeToMin(a.endTime);
    for (let j = i + 1; j < tasks.length; j++) {
      const b = tasks[j];
      if (!b.startTime || !b.endTime) continue;
      const bStart = timeToMin(b.startTime);
      const bEnd = timeToMin(b.endTime);
      if (aStart < bEnd && bStart < aEnd) conflicts++;
    }
  }
  return conflicts;
}

export function DayView({
  date,
  entries,
  selectedEmployeeIds,
  selectableEmployees,
  rangeStartHour = DEFAULT_RANGE.startHour,
  rangeEndHour = DEFAULT_RANGE.endHour,
  onAddTask,
  onTaskClick,
  onAddWorkDay,
  onDueTaskDrop,
}: DayViewProps) {
  const { t } = useTranslation('planning');
  const dateStr = format(date, 'yyyy-MM-dd');
  const todayStr = format(new Date(), 'yyyy-MM-dd');
  const isToday = dateStr === todayStr;

  const axis = useMemo(
    () => buildAxis(rangeStartHour, rangeEndHour),
    [rangeStartHour, rangeEndHour],
  );

  const { rows, stats } = useMemo(() => {
    const dayEntries = entries
      .filter((e) => e.date === dateStr)
      .filter((e) => !!e.employeeId && selectedEmployeeIds.includes(e.employeeId!));

    const byEmployee = new Map<string, Entry>();
    for (const e of dayEntries) {
      if (e.employeeId && !byEmployee.has(e.employeeId)) byEmployee.set(e.employeeId, e);
    }

    type RowItem = {
      employeeId: string;
      employeeName: string;
      avatarColor?: string | null;
      isBackOffice: boolean;
      entry?: Entry;
    };
    const rowItems: RowItem[] = [];

    const seen = new Set<string>();
    if (selectableEmployees && selectableEmployees.length > 0) {
      for (const emp of selectableEmployees) {
        if (!selectedEmployeeIds.includes(emp.id)) continue;
        const entry = byEmployee.get(emp.id);
        rowItems.push({
          employeeId: emp.id,
          employeeName: emp.name,
          avatarColor: emp.avatarColor ?? entry?.employeeAvatarColor ?? null,
          isBackOffice: isBackOfficeRole(emp.role),
          entry,
        });
        seen.add(emp.id);
      }
    }
    for (const [empId, entry] of byEmployee) {
      if (seen.has(empId)) continue;
      rowItems.push({
        employeeId: empId,
        employeeName: entry.employeeName ?? 'Unknown',
        avatarColor: entry.employeeAvatarColor ?? null,
        isBackOffice: false,
        entry,
      });
    }

    rowItems.sort((a, b) => a.employeeName.localeCompare(b.employeeName));

    let active = 0;
    let planned = 0;
    let taskCount = 0;
    let conflictCount = 0;
    for (const r of rowItems) {
      const e = r.entry;
      if (!e || entryIsAbsence(e)) continue;
      active++;
      planned += e.plannedHours ?? 0;
      const tasks = e.tasks ?? [];
      taskCount += tasks.length;
      conflictCount += countOverlaps(tasks);
    }

    return {
      rows: rowItems,
      stats: {
        active,
        planned: Math.round((planned + Number.EPSILON) * 10) / 10,
        tasks: taskCount,
        conflicts: conflictCount,
      },
    };
  }, [entries, dateStr, selectedEmployeeIds, selectableEmployees]);

  return (
    <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
      <div className="px-4 py-2 flex items-center gap-4 flex-wrap text-[11px] text-ink-muted dark:text-text-light border-b border-gray-200 dark:border-gray-700 bg-surface-muted dark:bg-gray-800">
        <Stat label={t('summary.active')} value={String(stats.active)} />
        <Stat label={t('summary.planned')} value={`${stats.planned}h`} />
        <Stat label={t('summary.tasks')} value={String(stats.tasks)} />
        <Stat
          label={t('summary.conflicts')}
          value={String(stats.conflicts)}
          tone={stats.conflicts > 0 ? 'text-status-conflict' : undefined}
          icon={stats.conflicts > 0 ? <AlertTriangle className="w-3 h-3" /> : null}
        />
      </div>

      {rows.length === 0 ? (
        <div className="p-10 text-center text-ink-muted dark:text-text-light">
          <CalendarX className="w-10 h-10 mx-auto mb-2 opacity-50" />
          <p className="text-sm">{t('summary.noEntries')}</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <div className="min-w-[900px]">
            <TimeRuler hours={axis.hours} employeeColumnWidth={200} />
            <div>
              {rows.map((row) => (
                <TimelineRow
                  key={row.employeeId}
                  entry={row.entry}
                  employeeId={row.employeeId}
                  employeeName={row.employeeName}
                  employeeAvatarColor={row.avatarColor}
                  isBackOffice={row.isBackOffice}
                  date={dateStr}
                  axis={{ startMin: axis.startMin, total: axis.total, hours: axis.hours }}
                  isToday={isToday}
                  onTaskClick={onTaskClick}
                  onAddTask={onAddTask}
                  onAddWorkDay={onAddWorkDay}
                  onDueTaskDrop={onDueTaskDrop}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({
  label,
  value,
  tone,
  icon,
}: {
  label: string;
  value: string;
  tone?: string;
  icon?: React.ReactNode;
}) {
  return (
    <span className={`inline-flex items-center gap-1.5 ${tone ?? ''}`}>
      {icon}
      <span className="uppercase tracking-wide text-[10px]">{label}</span>
      <span className="text-sm font-semibold text-tabular text-ink dark:text-white">{value}</span>
    </span>
  );
}
